import { hashCanonicalJson } from '../quote-engine/canonical-json';
import { prepareProjectAudioNotes } from './project-audio-notes-preparation';
import { prepareProjectDocumentClassification } from './project-document-classification-preparation';
import { prepareProjectImageTextRouting } from './project-image-text-preparation';
import { prepareProjectPdfEvidence } from './project-pdf-evidence-preparation';
import { prepareProjectVideoEvidence } from './project-video-evidence-preparation';
import {
    assertEvidencePreparationCurrent,
    type ProjectEvidencePreparationOptions, type ProjectEvidencePreparationResult,
} from './project-evidence-preparation-types';

export type { ProjectEvidencePreparationOptions, ProjectEvidencePreparationResult } from './project-evidence-preparation-types';

/** Both attended Summary and Quote Run await this before readiness/freshness checks. */
export async function prepareProjectEvidence(options: ProjectEvidencePreparationOptions): Promise<ProjectEvidencePreparationResult> {
    assertEvidencePreparationCurrent(options);
    // First, as a Stage 1 drop does: an image that is a document joins the documents.
    const images = await prepareProjectImageTextRouting(options);
    assertEvidencePreparationCurrent(options);
    const pdf = await prepareProjectPdfEvidence({ ...options, project: images.project });
    assertEvidencePreparationCurrent(options);
    // After the PDF text is read: the AI class a Stage 1 drop gives its documents.
    const classified = await prepareProjectDocumentClassification({ ...options, project: pdf.project });
    assertEvidencePreparationCurrent(options);
    // A folder import's recordings reach Inspector Notes, as a Stage 1 drop's do.
    const audio = await prepareProjectAudioNotes({ ...options, project: classified.project });
    assertEvidencePreparationCurrent(options);
    const video = await prepareProjectVideoEvidence({ ...options, project: audio.project });
    assertEvidencePreparationCurrent(options);
    return { project: video.project, changed: images.changed || pdf.changed || classified.changed || audio.changed || video.changed };
}

/** Stable evidence projection across inline → owned blob/S3 transport during save. */
export function getProjectEvidencePreparationFingerprint(project: Record<string, any>): string {
    const projection = {
        projectId: project.id,
        documents: (project.documents || []).filter((doc: any) => doc.evidencePreparation).map((doc: any) => ({
            id: doc.id, receipt: doc.evidencePreparation,
            content: doc.content || '', text: doc.text || '', extractedText: doc.extractedText || '',
            pdfEvidence: doc.pdfEvidence || null, documentVisualEvidence: doc.documentVisualEvidence || null,
        })),
        videos: (project.videos || []).filter((video: any) => video.evidencePreparation).map((video: any) => ({
            id: video.id, receipt: video.evidencePreparation, transcript: video.transcript || '',
        })),
        transcripts: (project.videoTranscripts || []).filter((row: any) => row.evidencePreparation).map((row: any) => ({
            videoId: row.videoId, text: row.text || '', fullText: row.fullText || '', receipt: row.evidencePreparation,
        })),
        frames: (project.photos || []).filter((photo: any) => photo.evidencePreparationHash).map((photo: any) => ({
            id: photo.id, videoId: photo.videoId, hash: photo.evidencePreparationHash,
            label: photo.label || '', context: photo.context || '', timestamp: photo.timestamp ?? null,
        })),
    };
    // Optional fields omitted by the normal JSON persistence boundary must not
    // make an otherwise identical saved receipt fail canonical validation.
    return hashCanonicalJson(JSON.parse(JSON.stringify(projection)));
}
